import React, { memo } from 'react';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import IconLabel from 'components/common/IconLabel';
import JlifeIcon from 'components/common/JlifeIcon';
import Avatar from 'components/common/Avatar';
import Label from 'components/common/Label';
import NumberPicker from 'components/common/NumberPicker';
import { formatPrice } from 'utils/format';
import { useRouter } from 'next/router';

interface IProps {
  itemType: string;
  disabled?: boolean;
  currency: string;
  orderEntity: any;
  count?: number;
  onAddOrderItem: (id: number) => void;
  onRemoveOrderItem: (id: number) => void;
  maxAmountReached: boolean;
}

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isProductionEnv = () => BUILD_ENV === 'production';

const useStyles = makeStyles()(() => ({
  priceOpacity: {
    opacity: 0.4,
  },
}));

const OrderItem: React.FC<React.PropsWithChildren<IProps>> = (props) => {
  const { classes } = useStyles();
  const [t] = useTranslation('public');
  const {
    itemType,
    currency,
    orderEntity,
    count = 0,
    disabled,
    onAddOrderItem,
    onRemoveOrderItem,
    maxAmountReached,
  } = props;
  const { id, title, description, price, max_amount_per_order, sold_out, min_amount_per_order } = orderEntity;
  const router = useRouter();
  const eventId = Number(router.query.eventId);

  const handleIncrement = () => {
    onAddOrderItem(id);
  };

  const handleDecrement = () => {
    onRemoveOrderItem(id);
  };

  const getPrice = () => {
    if (eventId === 6047 && isProductionEnv() && !price) return '';
    if (eventId === 1953 && isTestEnv() && !price) return '';
    return formatPrice(price, currency, true, true);
  };

  return (
    <Grid container spacing={2}>
      <Grid style={{ paddingLeft: 12 }} item xs={12}>
        <IconLabel label={title} labelTypographyProps={{ variant: 'subtitle1' }}>
          <Avatar variant="rounded" small>
            <JlifeIcon>{itemType}</JlifeIcon>
          </Avatar>
        </IconLabel>
      </Grid>
      {description && (
        <Grid style={{ paddingLeft: 20 }} item xs={12}>
          <Typography variant="body1">{description}</Typography>
        </Grid>
      )}
      <Grid style={{ paddingLeft: 20 }} item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" className={sold_out ? classes.priceOpacity : ''}>
              {getPrice()}
            </Typography>
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              {Boolean(min_amount_per_order) && itemType === 'ticket' && (
                <Grid item>
                  <Typography variant="caption" color="primary">
                    {t<string>('common:general.minOrderItem', { context: itemType, count: min_amount_per_order })}
                  </Typography>
                </Grid>
              )}
              <Grid item>
                {sold_out ? (
                  <Label
                    color="default"
                    textColor="gray"
                    label={t<string>('common:general.ticketsSaleStatus_sold_out')}
                  />
                ) : (
                  <NumberPicker
                    id={`${itemType}-number-picker-${id}`}
                    value={count}
                    max={max_amount_per_order}
                    min={min_amount_per_order}
                    edge="end"
                    disabled={disabled}
                    dense
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    maxAmountReached={maxAmountReached}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default memo(OrderItem);
